import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { documentLogger } from '../utils/logger';
// Temporarily handle RNFS import error
let RNFS: any;
try {
  RNFS = require('react-native-fs');
} catch (error) {
  documentLogger.warn('RNFS not available, using mock implementation:', error);
  // Mock RNFS for temporary functionality
  RNFS = {
    CachesDirectoryPath: '/tmp/cache',
    exists: () => Promise.resolve(false),
    copyFile: () => Promise.resolve(),
    readFile: () => Promise.resolve('mock_content'),
    writeFile: () => Promise.resolve(),
    unlink: () => Promise.resolve(),
  };
}
import CryptoJS from 'crypto-js';
import * as Keychain from 'react-native-keychain';
import { vaultService } from './vaultService';
import {
  PBKDF2_ITERATIONS,
  decrypt as v2Decrypt,
  deriveKey,
  encrypt as v2Encrypt,
  generateKey as v2GenerateKey,
  isV2Envelope,
  parseEnvelope,
  serializeEnvelope,
} from './encryption/DocumentEncryptionV2';
import { getOrCreateUserSalt } from './encryption/UserSalt';
import {
  getCachedMasterKey,
  isWrappedMasterKeyEnrolled,
  VaultLockedError,
  RecoveryCodeRequiredError,
} from './encryption/MasterKeyService';

// Storage type for tiered document storage
// NONE: No document storage (trial/basic tiers)
// LOCAL: 500MB of documents ($59 Plus tier)
// CLOUD: 2GB of documents ($99 Premium tier)
// Both keep an encrypted copy on the phone and on the server (Phase 5);
// the server enforces the quota, the phone's check is advisory.
export type StorageType = 'none' | 'local' | 'cloud';

// Types
export interface DocumentMetadata {
  documentId: string;
  category: string;
  documentType: string;
  fileName: string;
  fileSize: number;
  checksum: string;
  encryptedDocumentKey: string; // Encrypted with master recovery key
  uploadedAt: string;
  tags: string[];
  storageLocation?: StorageType; // Where the document is stored (local/cloud)
  /** The picked file's MIME type, so the OS viewer and the web know how to open it. */
  mimeType?: string;
  /** The account that saved this entry on this phone. */
  ownerUserId?: string;
  /** Set when the server holds this version of the file (documentFileSync). */
  syncedAt?: string;
}

export interface StorageResult {
  fileId: string;
  fileUrl: string;
  checksum: string;
  size: number;
  success: boolean;
  error?: string;
}

export interface FileInfo {
  uri: string;
  name: string;
  size: number;
  type: string;
}

// Abstract base class for storage providers
export abstract class DocumentStorageProvider {
  abstract storeFile(file: FileInfo, metadata: DocumentMetadata): Promise<StorageResult>;
  abstract retrieveFile(fileId: string): Promise<FileInfo>;
  abstract deleteFile(fileId: string): Promise<boolean>;
  abstract listFiles(category?: string): Promise<DocumentMetadata[]>;
  abstract getFileUrl(fileId: string): Promise<string>;
  // Updates a document's metadata in-place without re-encrypting the file.
  // Used by the edit flow so the user can rename / re-categorize / re-tag
  // an existing digital-copy document without having to re-upload it.
  abstract updateMetadata(fileId: string, updates: Partial<DocumentMetadata>): Promise<boolean>;
}

/** Where document files live on the phone: Documents, not Caches (iOS may evict Caches). */
export const DOCUMENTS_DIR = `${RNFS.DocumentDirectoryPath}/documents`;

/** A registry entry: the document's metadata plus where its encrypted file is. */
export type LocalDocumentEntry = DocumentMetadata & {
  /** The encrypted file's name in DOCUMENTS_DIR ('' until a cloud-only document is downloaded). */
  fileUrl: string;
};

/**
 * Thrown when a document's encrypted file is not on this phone — a document
 * added on the web that has not been downloaded yet, or one whose file was
 * lost. documentFileSync.ensureLocal fetches it when the server holds it.
 */
export class DocumentFileMissingError extends Error {
  constructor(message = "This document's file is not on this phone.") {
    super(message);
    this.name = 'DocumentFileMissingError';
  }
}

/**
 * A file-system path for a picked file. The document picker (keepLocalCopy)
 * and the photo library hand back `file://` URLs, percent-encoded: a file
 * named "Fitment Design.pdf" arrives as `.../Fitment%20Design.pdf`. RNFS
 * drops the scheme but not the encoding, so the read fails with ENOENT
 * (2026-09-22). Plain paths pass through untouched.
 */
export function toFilePath(uri: string): string {
  if (!uri.startsWith('file://')) return uri;
  const path = uri.slice('file://'.length);
  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
}

/** The signed-in user's id, or null when nobody is signed in. */
export async function getSignedInUserId(): Promise<string | null> {
  try {
    const id = await AsyncStorage.getItem('@user_id');
    if (id) return id;
    const raw = await AsyncStorage.getItem('user');
    return raw ? JSON.parse(raw)?.id ?? null : null;
  } catch {
    return null;
  }
}

// Local storage provider: the phone's encrypted copy of every document.
export class LocalDocumentStorage extends DocumentStorageProvider {
  private storageKey = 'local_documents';

  /**
   * Where the entry's encrypted file is today, or null if it is gone.
   *
   * Entries hold the file's name; older ones hold an absolute path under
   * Caches. Never use either directly — iOS renames the app's container on
   * every reinstall and Xcode build, so an absolute path goes stale while
   * the file survives. A file still in Caches is moved to Documents here.
   */
  async resolveFilePath(fileUrl: string | undefined): Promise<string | null> {
    if (!fileUrl) return null;
    const name = fileUrl.split('/').pop() || fileUrl;
    const current = `${DOCUMENTS_DIR}/${name}`;
    if (await RNFS.exists(current)) return current;
    const legacy = `${RNFS.CachesDirectoryPath}/${name}`;
    if (await RNFS.exists(legacy)) {
      try {
        await this.ensureDir();
        await RNFS.moveFile(legacy, current);
        return current;
      } catch (error) {
        documentLogger.warn('Could not move a document out of Caches:', error);
        return legacy;
      }
    }
    return null;
  }

  private async ensureDir(): Promise<void> {
    if (!(await RNFS.exists(DOCUMENTS_DIR))) await RNFS.mkdir(DOCUMENTS_DIR);
  }

  /** Write an already-encrypted envelope as the document's file; returns the file name. */
  async writeEncryptedFile(fileId: string, sourcePath: string | null, contents?: string): Promise<string> {
    await this.ensureDir();
    const name = `${fileId}_${Date.now()}.enc`;
    const path = `${DOCUMENTS_DIR}/${name}`;
    if (sourcePath) {
      await RNFS.copyFile(sourcePath, path);
    } else {
      await RNFS.writeFile(path, contents ?? '', 'utf8');
    }
    return name;
  }

  async storeFile(file: FileInfo, metadata: DocumentMetadata): Promise<StorageResult> {
    try {
      // Always use the provided documentId as the storage key
      const fileId = metadata.documentId;
      if (!fileId) {
        throw new Error('Document ID is required');
      }

      const name = await this.writeEncryptedFile(fileId, file.uri);
      const documents = await this.getStoredDocuments();
      const previous = documents[fileId];

      // A fresh entry with no syncedAt, so the new file uploads (and
      // replaces the server's copy when this is an edit with a new file).
      documents[fileId] = {
        ...metadata,
        fileUrl: name,
        ownerUserId: metadata.ownerUserId ?? (await getSignedInUserId()) ?? undefined,
        uploadedAt: new Date().toISOString(),
      };
      await AsyncStorage.setItem(this.storageKey, JSON.stringify(documents));

      if (previous?.fileUrl) {
        const oldPath = await this.resolveFilePath(previous.fileUrl);
        if (oldPath && !oldPath.endsWith(`/${name}`)) {
          await RNFS.unlink(oldPath).catch(() => undefined);
        }
      }

      return {
        fileId,
        fileUrl: name,
        checksum: metadata.checksum,
        size: file.size,
        success: true
      };
    } catch (error) {
      documentLogger.error('Error storing file locally:', error);
      return {
        fileId: '',
        fileUrl: '',
        checksum: '',
        size: 0,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  async retrieveFile(fileId: string): Promise<FileInfo> {
    const document = await this.getEntry(fileId);
    if (!document) {
      throw new Error('File not found');
    }
    const path = await this.resolveFilePath(document.fileUrl);
    if (!path) {
      throw new DocumentFileMissingError();
    }
    return {
      uri: path,
      name: document.fileName,
      size: document.fileSize,
      type: document.mimeType || document.documentType
    };
  }

  async deleteFile(fileId: string): Promise<boolean> {
    try {
      const documents = await this.getStoredDocuments();
      const document = documents[fileId];

      if (document?.fileUrl) {
        const path = await this.resolveFilePath(document.fileUrl);
        if (path) await RNFS.unlink(path);
      }

      delete documents[fileId];
      await AsyncStorage.setItem(this.storageKey, JSON.stringify(documents));

      return true;
    } catch (error) {
      documentLogger.error('Error deleting file locally:', error);
      return false;
    }
  }

  async updateMetadata(fileId: string, updates: Partial<DocumentMetadata>): Promise<boolean> {
    try {
      const documents = await this.getStoredDocuments();
      if (!documents[fileId]) {
        documentLogger.warn('🔍 updateMetadata called for unknown fileId:', fileId);
        return false;
      }
      // Preserve fileUrl, encryptedDocumentKey, checksum, uploadedAt — the
      // edit flow should never touch the encrypted-file pointer or its
      // crypto state. Only user-editable fields should land here.
      const { fileUrl, encryptedDocumentKey, checksum, uploadedAt, ...safeUpdates } =
        updates as DocumentMetadata & { fileUrl?: string };
      documents[fileId] = {
        ...documents[fileId],
        ...safeUpdates,
      };
      await AsyncStorage.setItem(this.storageKey, JSON.stringify(documents));
      return true;
    } catch (error) {
      documentLogger.error('Error updating metadata locally:', error);
      return false;
    }
  }

  /**
   * The signed-in user's documents. Entries another account saved on this
   * phone are hidden; entries saved before owners were recorded stay
   * visible until documentFileSync claims them.
   */
  async listFiles(category?: string): Promise<DocumentMetadata[]> {
    try {
      const documents = await this.getStoredDocuments();
      const userId = await getSignedInUserId();
      const fileList = Object.entries(documents)
        .filter(([, doc]) => !doc.ownerUserId || doc.ownerUserId === userId)
        .map(([key, doc]) => ({
          ...doc,
          documentId: key // Ensure documentId matches the storage key
        }));
      return category ? fileList.filter(doc => doc.category === category) : fileList;
    } catch (error) {
      documentLogger.error('Error listing files locally:', error);
      return [];
    }
  }

  async getFileUrl(fileId: string): Promise<string> {
    const document = await this.getEntry(fileId);
    const path = document ? await this.resolveFilePath(document.fileUrl) : null;
    if (!path) {
      throw new Error('File not found');
    }
    return `file://${path}`;
  }

  // ---- Registry access for documentFileSync ---------------------------------

  async getEntry(fileId: string): Promise<LocalDocumentEntry | null> {
    const documents = await this.getStoredDocuments();
    return documents[fileId] ? { ...documents[fileId], documentId: fileId } : null;
  }

  async getAllEntries(): Promise<Record<string, LocalDocumentEntry>> {
    return this.getStoredDocuments();
  }

  /** Merge fields into an entry (sync state, server metadata); creates it when absent. */
  async patchEntry(fileId: string, patch: Partial<LocalDocumentEntry>): Promise<void> {
    const documents = await this.getStoredDocuments();
    documents[fileId] = { ...(documents[fileId] ?? {}), ...patch, documentId: fileId } as LocalDocumentEntry;
    await AsyncStorage.setItem(this.storageKey, JSON.stringify(documents));
  }

  /** Drop an entry and its file without touching the server. */
  async removeEntry(fileId: string): Promise<void> {
    await this.deleteFile(fileId);
  }

  private async getStoredDocuments(): Promise<Record<string, LocalDocumentEntry>> {
    try {
      const stored = await AsyncStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      documentLogger.error('Error reading stored documents:', error);
      return {};
    }
  }
}

/** The phone's one local store, shared by the service and documentFileSync. */
export const localDocumentStorage = new LocalDocumentStorage();

// S3 storage provider (for production)
export class S3DocumentStorage extends DocumentStorageProvider {
  private bucketName: string;
  private region: string;
  private accessKeyId: string;
  private secretAccessKey: string;

  constructor(config: {
    bucketName: string;
    region: string;
    accessKeyId: string;
    secretAccessKey: string;
  }) {
    super();
    this.bucketName = config.bucketName;
    this.region = config.region;
    this.accessKeyId = config.accessKeyId;
    this.secretAccessKey = config.secretAccessKey;
  }

  async storeFile(file: FileInfo, metadata: DocumentMetadata): Promise<StorageResult> {
    // TODO: Implement S3 upload logic
    // This would use AWS SDK or similar
    throw new Error('S3 storage not yet implemented');
  }

  async retrieveFile(fileId: string): Promise<FileInfo> {
    // TODO: Implement S3 download logic
    throw new Error('S3 storage not yet implemented');
  }

  async deleteFile(fileId: string): Promise<boolean> {
    // TODO: Implement S3 delete logic
    throw new Error('S3 storage not yet implemented');
  }

  async listFiles(category?: string): Promise<DocumentMetadata[]> {
    // TODO: Implement S3 list logic
    throw new Error('S3 storage not yet implemented');
  }

  async getFileUrl(fileId: string): Promise<string> {
    // TODO: Implement S3 URL generation
    throw new Error('S3 storage not yet implemented');
  }

  async updateMetadata(fileId: string, updates: Partial<DocumentMetadata>): Promise<boolean> {
    // TODO: Implement S3 metadata update logic
    throw new Error('S3 storage not yet implemented');
  }
}

// Storage tier configuration
const STORAGE_TIER_CONFIG = {
  none: {
    limitBytes: 0,
    limitMB: 0,
    canUpload: false,
    canUploadToCloud: false,
    description: 'No document storage',
    upgradeMessage: 'Upgrade to Plus ($59/year) for local document storage',
  },
  local: {
    limitBytes: 500 * 1024 * 1024, // 500MB
    limitMB: 500,
    canUpload: true,
    canUploadToCloud: false,
    description: 'Documents (500MB)',
    upgradeMessage: 'Upgrade to Premium ($99/year) for 2GB of documents',
  },
  cloud: {
    limitBytes: 2 * 1024 * 1024 * 1024, // 2GB
    limitMB: 2048,
    canUpload: true,
    canUploadToCloud: true,
    description: 'Documents (2GB)',
    upgradeMessage: null,
  },
};

// Main document storage service
export class DocumentStorageService {
  private provider: DocumentStorageProvider;
  private encryptionService: DocumentEncryptionService;
  private storageType: StorageType;

  constructor(provider: DocumentStorageProvider, storageType: StorageType = 'local') {
    this.provider = provider;
    this.encryptionService = new DocumentEncryptionService();
    this.storageType = storageType;
  }

  /**
   * Update the storage type based on user's subscription tier
   * Call this when subscription changes
   */
  setStorageType(storageType: StorageType): void {
    this.storageType = storageType;
    documentLogger.debug(`📦 Storage type set to: ${storageType}`);
  }

  /**
   * Get current storage type
   */
  getStorageType(): StorageType {
    return this.storageType;
  }

  /**
   * Check if uploads are allowed for current storage tier
   */
  canUpload(): boolean {
    return STORAGE_TIER_CONFIG[this.storageType].canUpload;
  }

  /**
   * Check if cloud uploads are allowed for current storage tier
   */
  canUploadToCloud(): boolean {
    return STORAGE_TIER_CONFIG[this.storageType].canUploadToCloud;
  }

  /**
   * Get storage tier configuration for current tier
   */
  getStorageTierConfig() {
    return STORAGE_TIER_CONFIG[this.storageType];
  }

  /**
   * Get storage limit based on current tier
   */
  getStorageLimit(): { bytes: number; mb: number } {
    const config = STORAGE_TIER_CONFIG[this.storageType];
    return { bytes: config.limitBytes, mb: config.limitMB };
  }

  /**
   * Get upgrade message for current tier (if applicable)
   */
  getUpgradeMessage(): string | null {
    return STORAGE_TIER_CONFIG[this.storageType].upgradeMessage;
  }

  async storeDocument(file: FileInfo, metadata: Omit<DocumentMetadata, 'fileId' | 'uploadedAt' | 'checksum' | 'encryptedDocumentKey'>): Promise<StorageResult> {
    try {
      // Check if storage tier allows uploads
      if (!this.canUpload()) {
        documentLogger.warn('📦 Upload rejected: storage tier does not allow uploads');
        return {
          fileId: '',
          fileUrl: '',
          checksum: '',
          size: 0,
          success: false,
          error: STORAGE_TIER_CONFIG[this.storageType].upgradeMessage || 'Document storage not available for your subscription tier',
        };
      }

      // Check storage limit before uploading
      const canAdd = await this.canAddFile(file.size);
      if (!canAdd.canAdd) {
        documentLogger.warn('📦 Upload rejected: would exceed storage limit');
        return {
          fileId: '',
          fileUrl: '',
          checksum: '',
          size: 0,
          success: false,
          error: canAdd.reason,
        };
      }

      // Documents are kept only under a Recovery Code (throws
      // RecoveryCodeRequiredError or VaultLockedError for the screen).
      const masterKey = await this.encryptionService.masterKeyForNewDocument();

      // Generate checksum
      const checksum = await this.generateChecksum(file);
      
      // Generate document key and encrypt it with master key
      const { encryptedKey, documentKey } = await this.encryptionService.generateDocumentKey(masterKey);
      
      // Encrypt file content with document key
      const encryptedFile = await this.encryptionService.encryptFile(file, documentKey);
      
      // Prepare metadata with encrypted document key
      const fullMetadata: DocumentMetadata = {
        ...metadata,
        mimeType: metadata.mimeType || file.type,
        encryptedDocumentKey: encryptedKey,
        checksum,
        uploadedAt: new Date().toISOString()
      };
      
      // Store encrypted file
      const result = await this.provider.storeFile(encryptedFile, fullMetadata);
      
      // Clean up temporary encrypted file
      await this.encryptionService.cleanupTempFile(encryptedFile.uri);
      
      return result;
    } catch (error) {
      // The screen offers the Recovery Code or unlock flow for these.
      if (error instanceof RecoveryCodeRequiredError || error instanceof VaultLockedError) {
        throw error;
      }
      documentLogger.error('Error storing document:', error);
      return {
        fileId: '',
        fileUrl: '',
        checksum: '',
        size: 0,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  async retrieveDocument(fileId: string): Promise<FileInfo> {
    try {
      // Get master recovery key
      let masterKey = await this.encryptionService.getMasterRecoveryKey();
      if (!masterKey) {
        // This shouldn't happen if document was stored, but handle gracefully
        documentLogger.debug('No master key found for retrieval, auto-generating...');
        masterKey = await this.encryptionService.autoGenerateMasterKey();
      }

      // Retrieve encrypted file and metadata
      const encryptedFile = await this.provider.retrieveFile(fileId);
      
      // Get the document metadata directly from the storage provider
      // Since we're now using documentId as the key, we can get it directly
      const storedDocuments = await this.provider.listFiles();
      const document = storedDocuments.find(doc => doc.documentId === fileId);
      
      if (!document) {
        throw new Error('Document metadata not found');
      }

      // Decrypt document key using master key
      const documentKey = await this.encryptionService.decryptDocumentKey(
        document.encryptedDocumentKey, 
        masterKey
      );
      
      // Decrypt file content
      const decryptedFile = await this.encryptionService.decryptFile(encryptedFile, documentKey);
      
      return decryptedFile;
    } catch (error) {
      documentLogger.error('Error retrieving document:', error);
      throw error;
    }
  }

  async deleteDocument(fileId: string): Promise<boolean> {
    try {
      return await this.provider.deleteFile(fileId);
    } catch (error) {
      documentLogger.error('Error deleting document:', error);
      return false;
    }
  }

  /**
   * Update metadata on an existing digital-copy document without
   * re-uploading the encrypted file. Used by the edit flow when the user
   * changes name / category / type / tags but keeps the same file.
   */
  async updateDocumentMetadata(
    fileId: string,
    updates: Partial<DocumentMetadata>,
  ): Promise<boolean> {
    try {
      return await this.provider.updateMetadata(fileId, updates);
    } catch (error) {
      documentLogger.error('Error updating document metadata:', error);
      return false;
    }
  }

  async listDocuments(category?: string): Promise<DocumentMetadata[]> {
    try {
      return await this.provider.listFiles(category);
    } catch (error) {
      documentLogger.error('Error listing documents:', error);
      return [];
    }
  }

  async getDocumentUrl(fileId: string): Promise<string> {
    try {
      return await this.provider.getFileUrl(fileId);
    } catch (error) {
      documentLogger.error('Error getting document URL:', error);
      throw error;
    }
  }

  private async generateChecksum(file: FileInfo): Promise<string> {
    try {
      if (Platform.OS === 'web') {
        // For web, we'd need to handle file reading differently
        return 'web_checksum_placeholder';
      } else {
        // Generate real SHA-256 checksum
        documentLogger.debug('🔍 Generating SHA-256 checksum for file:', file.name);
        
        // Read file content as base64 to handle binary files properly
        const fileContent = await RNFS.readFile(toFilePath(file.uri), 'base64');
        
        // Generate SHA-256 hash using CryptoJS (base64 is safe for hashing)
        const hash = CryptoJS.SHA256(fileContent);
        const checksum = hash.toString();
        
        documentLogger.debug('🔍 SHA-256 checksum generated:', checksum.substring(0, 16) + '...');
        return checksum;
      }
    } catch (error) {
      documentLogger.error('Error generating checksum:', error);
      return 'checksum_error';
    }
  }

  private simpleHash(str: string): string {
    let hash = 0;
    if (str.length === 0) return hash.toString();
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString();
  }

  /**
   * Calculate total storage used by all documents
   * @returns Total size in bytes
   */
  async getTotalStorageUsed(): Promise<number> {
    try {
      const documents = await this.listDocuments();
      const totalBytes = documents.reduce((sum, doc) => sum + doc.fileSize, 0);
      documentLogger.debug(`📊 Total storage used: ${(totalBytes / 1024 / 1024).toFixed(2)} MB`);
      return totalBytes;
    } catch (error) {
      documentLogger.error('Error calculating total storage:', error);
      return 0;
    }
  }

  /**
   * Get storage usage information
   * @returns Storage stats including used, limit, and percentage
   */
  async getStorageStats(): Promise<{
    usedBytes: number;
    usedMB: number;
    limitBytes: number;
    limitMB: number;
    percentageUsed: number;
    remaining: number;
    documentCount: number;
    storageType: StorageType;
    canUpload: boolean;
    canUploadToCloud: boolean;
    upgradeMessage: string | null;
  }> {
    try {
      // Use tier-based limits
      const tierConfig = STORAGE_TIER_CONFIG[this.storageType];
      const LIMIT_BYTES = tierConfig.limitBytes;
      const LIMIT_MB = tierConfig.limitMB;

      const documents = await this.listDocuments();
      const usedBytes = documents.reduce((sum, doc) => sum + doc.fileSize, 0);
      const usedMB = usedBytes / 1024 / 1024;
      const percentageUsed = LIMIT_BYTES > 0 ? (usedBytes / LIMIT_BYTES) * 100 : 0;
      const remaining = Math.max(0, LIMIT_BYTES - usedBytes);

      return {
        usedBytes,
        usedMB: parseFloat(usedMB.toFixed(2)),
        limitBytes: LIMIT_BYTES,
        limitMB: LIMIT_MB,
        percentageUsed: parseFloat(percentageUsed.toFixed(1)),
        remaining,
        documentCount: documents.length,
        storageType: this.storageType,
        canUpload: tierConfig.canUpload,
        canUploadToCloud: tierConfig.canUploadToCloud,
        upgradeMessage: tierConfig.upgradeMessage,
      };
    } catch (error) {
      documentLogger.error('Error getting storage stats:', error);
      const tierConfig = STORAGE_TIER_CONFIG[this.storageType];
      return {
        usedBytes: 0,
        usedMB: 0,
        limitBytes: tierConfig.limitBytes,
        limitMB: tierConfig.limitMB,
        percentageUsed: 0,
        remaining: tierConfig.limitBytes,
        documentCount: 0,
        storageType: this.storageType,
        canUpload: tierConfig.canUpload,
        canUploadToCloud: tierConfig.canUploadToCloud,
        upgradeMessage: tierConfig.upgradeMessage,
      };
    }
  }

  /**
   * Check if storage limit would be exceeded by adding a new file
   * @param fileSize Size of file to be added in bytes
   * @returns true if within limit, false if would exceed
   */
  async canAddFile(fileSize: number): Promise<{
    canAdd: boolean;
    reason?: string;
    usedMB: number;
    limitMB: number;
    fileSizeMB: number;
    storageType: StorageType;
    upgradeMessage: string | null;
  }> {
    try {
      // First check if storage tier allows uploads at all
      const tierConfig = STORAGE_TIER_CONFIG[this.storageType];
      if (!tierConfig.canUpload) {
        return {
          canAdd: false,
          reason: tierConfig.upgradeMessage || 'Document storage not available for your subscription tier',
          usedMB: 0,
          limitMB: tierConfig.limitMB,
          fileSizeMB: fileSize / 1024 / 1024,
          storageType: this.storageType,
          upgradeMessage: tierConfig.upgradeMessage,
        };
      }

      const stats = await this.getStorageStats();
      const fileSizeMB = fileSize / 1024 / 1024;
      const totalAfterAdd = stats.usedBytes + fileSize;
      const canAdd = totalAfterAdd <= stats.limitBytes;

      if (!canAdd) {
        const tierDescription = this.storageType === 'local' ? 'Plus (500MB)' : 'Premium (2GB)';
        return {
          canAdd: false,
          reason: `Adding this file would exceed the ${tierDescription} storage limit. Current: ${stats.usedMB}MB, File: ${fileSizeMB.toFixed(2)}MB`,
          usedMB: stats.usedMB,
          limitMB: stats.limitMB,
          fileSizeMB: parseFloat(fileSizeMB.toFixed(2)),
          storageType: this.storageType,
          upgradeMessage: tierConfig.upgradeMessage,
        };
      }

      return {
        canAdd: true,
        usedMB: stats.usedMB,
        limitMB: stats.limitMB,
        fileSizeMB: parseFloat(fileSizeMB.toFixed(2)),
        storageType: this.storageType,
        upgradeMessage: tierConfig.upgradeMessage,
      };
    } catch (error) {
      documentLogger.error('Error checking storage limit:', error);
      const tierConfig = STORAGE_TIER_CONFIG[this.storageType];
      return {
        canAdd: false,
        reason: 'Error checking storage limit',
        usedMB: 0,
        limitMB: tierConfig.limitMB,
        fileSizeMB: fileSize / 1024 / 1024,
        storageType: this.storageType,
        upgradeMessage: tierConfig.upgradeMessage,
      };
    }
  }
}

/**
 * Thrown when a caller tries to read a v1-format blob. Callers should route
 * the user through the migration flow; there is no fallback decryption path.
 */
export class MigrationRequiredError extends Error {
  constructor(message = 'This item is in a legacy format and needs to be migrated.') {
    super(message);
    this.name = 'MigrationRequiredError';
  }
}

// Document encryption service — v2.
// Every encrypt path routes through DocumentEncryptionV2 (AES-256-CBC +
// HMAC-SHA-256, native PBKDF2 at 210k, per-user salt). Reads that encounter
// v1-format ciphertext throw MigrationRequiredError instead of falling back.
export class DocumentEncryptionService {
  private readonly MASTER_KEY_IDENTIFIER = 'document_master_key';
  private migrationAttempted = false;

  /**
   * Derive the master recovery key from a user secret code using v2
   * parameters (per-user salt, native PBKDF2-SHA256 at 210k iters). Stores
   * the derived key in Vault (primary) with a keychain backup for offline
   * reads.
   */
  async generateMasterRecoveryKey(secretCode: string): Promise<string> {
    if (!secretCode) {
      throw new Error('generateMasterRecoveryKey: secret code is required');
    }

    const salt = await getOrCreateUserSalt();
    const masterKey = await deriveKey(secretCode, salt, PBKDF2_ITERATIONS);

    await vaultService.storeMasterRecoveryKey(masterKey);

    try {
      await Keychain.setInternetCredentials(
        this.MASTER_KEY_IDENTIFIER,
        'master_key',
        masterKey,
      );
    } catch (keychainError) {
      documentLogger.debug('Keychain backup storage failed (non-critical)');
    }

    return masterKey;
  }

  /**
   * Return the master recovery key for document encryption. Priority:
   *
   *   1. Wrapped-key path (Step 5 activation). If the user has enrolled
   *      a recovery code, the unwrapped key is cached in the keychain
   *      via MasterKeyService. That cache is the source of truth for
   *      enrolled users — DO NOT fall back to the legacy path if it's
   *      empty, because that would encrypt new documents under a
   *      different key than the user's wrapped one and they couldn't
   *      decrypt them later. Instead, throw `VaultLockedError` so the
   *      caller can route to UnlockVaultScreen.
   *
   *   2. Legacy path (pre-Step-5 users who never enrolled). Try the
   *      backend Vault, then the device keychain backup, migrate
   *      keychain → Vault once if the Vault was empty but keychain
   *      wasn't.
   */
  async getMasterRecoveryKey(): Promise<string | null> {
    // Step 5 activation: if the user has enrolled a wrapped master key,
    // the device-cached unwrapped copy is authoritative. Don't touch
    // the legacy Vault path — its contents may be stale or absent.
    try {
      const enrolled = await isWrappedMasterKeyEnrolled();
      if (enrolled) {
        const cached = await getCachedMasterKey();
        if (cached) return cached;
        throw new VaultLockedError();
      }
    } catch (err) {
      if (err instanceof VaultLockedError) throw err;
      documentLogger.warn(
        'Wrapped-master-key enrollment check failed, trying legacy path',
      );
    }

    // Legacy path — users who haven't enrolled a recovery code yet.
    try {
      const vaultKey = await vaultService.getMasterRecoveryKey();
      if (vaultKey) return vaultKey;

      if (!this.migrationAttempted) {
        this.migrationAttempted = true;
        const keychainKey = await this.getKeyFromKeychain();
        if (keychainKey) {
          await vaultService.storeMasterRecoveryKey(keychainKey);
          return keychainKey;
        }
      }

      return null;
    } catch (error) {
      documentLogger.error('Error retrieving master recovery key:', error);
      return this.getKeyFromKeychain();
    }
  }

  /**
   * The master key for saving a new document. Documents are kept only
   * under a Recovery Code (2026-09-25): an account without one gets
   * RecoveryCodeRequiredError instead of a key the server can read, and
   * setting the code wraps the account's existing key (nothing is lost).
   * Fails closed: an enrollment check that cannot be answered throws.
   */
  async masterKeyForNewDocument(): Promise<string> {
    if (!(await isWrappedMasterKeyEnrolled())) {
      throw new RecoveryCodeRequiredError();
    }
    const key = await this.getMasterRecoveryKey();
    if (!key) throw new VaultLockedError();
    return key;
  }

  private async getKeyFromKeychain(): Promise<string | null> {
    try {
      const credentials = await Keychain.getInternetCredentials(this.MASTER_KEY_IDENTIFIER);
      return credentials ? credentials.password : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Generate and persist a fresh random master key — used when the user has
   * not set a secret code yet. No Math.random: backed by Aes.randomKey via
   * DocumentEncryptionV2.generateKey.
   *
   * Refuses to run for users who have already enrolled a wrapped master
   * key (generating a new one would orphan every document they've
   * already uploaded). Callers should route to UnlockVaultScreen instead.
   *
   * Both checks below fail CLOSED. Generating a master key is effectively
   * destructive for anyone who already has one, so "I could not determine
   * whether a key exists" must never be rounded down to "no key exists".
   * The previous `.catch(() => false)` did exactly that, which meant a
   * backend blip could orphan a pre-Step-5 user's entire vault.
   */
  async autoGenerateMasterKey(): Promise<string> {
    // 1. Wrapped (Step 5+) enrollment. Errors propagate — do not swallow.
    const enrolled = await isWrappedMasterKeyEnrolled();
    if (enrolled) {
      throw new VaultLockedError();
    }

    // 2. Legacy (pre-Step-5) master key. A user who enrolled before the
    // key-wrapping rewrite has no wrapped key, so check 1 passes for them
    // legitimately — their only key lives at the legacy path. Overwriting
    // it would make every document and vault item they own permanently
    // undecryptable. A throw here means "unknown", which also blocks.
    const legacyKey = await vaultService.getMasterRecoveryKey();
    if (legacyKey) {
      documentLogger.warn(
        'Refusing to auto-generate: a legacy master key already exists',
      );
      throw new VaultLockedError();
    }

    const masterKey = await v2GenerateKey();
    await vaultService.storeMasterRecoveryKey(masterKey);

    try {
      await Keychain.setInternetCredentials(
        this.MASTER_KEY_IDENTIFIER,
        'master_key',
        masterKey,
      );
    } catch (keychainError) {
      documentLogger.debug('Keychain backup storage failed (non-critical)');
    }

    return masterKey;
  }

  /**
   * Generate a fresh per-document key and return it alongside the key
   * wrapped (v2-encrypted) under the master key. The wrapped form is a
   * JSON-serialized v2 envelope suitable for writing to storage.
   */
  async generateDocumentKey(
    masterKey: string,
  ): Promise<{ encryptedKey: string; documentKey: string }> {
    const documentKey = await v2GenerateKey();
    const envelope = await v2Encrypt(documentKey, masterKey);
    return { encryptedKey: serializeEnvelope(envelope), documentKey };
  }

  /**
   * Encrypt a file's contents using v2 (AES-256-CBC + HMAC-SHA-256). Writes
   * the serialized v2 envelope JSON to a temp file as UTF-8 and returns the
   * FileInfo pointing to it. Callers must invoke cleanupTempFile after the
   * upload completes.
   */
  async encryptFile(file: FileInfo, documentKey: string): Promise<FileInfo> {
    const fileContentBase64 = await RNFS.readFile(toFilePath(file.uri), 'base64');
    const envelope = await v2Encrypt(fileContentBase64, documentKey);
    const serialized = serializeEnvelope(envelope);

    const tempDir = RNFS.CachesDirectoryPath;
    const encryptedFileName = `encrypted_${Date.now()}_${file.name}`;
    const encryptedFilePath = `${tempDir}/${encryptedFileName}`;

    await RNFS.writeFile(encryptedFilePath, serialized, 'utf8');

    return {
      ...file,
      uri: encryptedFilePath,
      size: serialized.length,
    };
  }

  /**
   * Decrypt a file previously encrypted with `encryptFile`. v1-format inputs
   * throw MigrationRequiredError — callers must route through the migration
   * flow rather than attempting a fallback.
   */
  async decryptFile(file: FileInfo, documentKey: string): Promise<FileInfo> {
    const encryptedContent = await RNFS.readFile(toFilePath(file.uri), 'utf8');
    if (!isV2Envelope(encryptedContent)) {
      throw new MigrationRequiredError();
    }
    const envelope = parseEnvelope(encryptedContent);
    const plaintextBase64 = await v2Decrypt(envelope, documentKey);

    const tempDir = RNFS.CachesDirectoryPath;
    const decryptedFileName = `decrypted_${Date.now()}_${file.name}`;
    const decryptedFilePath = `${tempDir}/${decryptedFileName}`;
    await RNFS.writeFile(decryptedFilePath, plaintextBase64, 'base64');

    return {
      ...file,
      uri: decryptedFilePath,
      size: plaintextBase64.length,
    };
  }

  /**
   * Unwrap a v2-wrapped document key using the master key. v1-format wrapped
   * keys throw MigrationRequiredError.
   */
  async decryptDocumentKey(encryptedKey: string, masterKey: string): Promise<string> {
    if (!isV2Envelope(encryptedKey)) {
      throw new MigrationRequiredError();
    }
    const envelope = parseEnvelope(encryptedKey);
    return v2Decrypt(envelope, masterKey);
  }

  // Clean up temporary files
  async cleanupTempFile(filePath: string): Promise<void> {
    try {
      const exists = await RNFS.exists(filePath);
      if (exists) {
        await RNFS.unlink(filePath);
      }
    } catch (error) {
      documentLogger.error('Error cleaning up temp file:', error);
    }
  }

  private async generateChecksum(file: FileInfo): Promise<string> {
    try {
      if (Platform.OS === 'web') {
        return 'web_checksum_placeholder';
      }
      const fileContent = await RNFS.readFile(toFilePath(file.uri), 'base64');
      const hash = CryptoJS.SHA256(fileContent);
      return hash.toString();
    } catch (error) {
      documentLogger.error('Error generating checksum:', error);
      return 'checksum_error';
    }
  }

  async verifyFileIntegrity(file: FileInfo, expectedChecksum: string): Promise<boolean> {
    try {
      const currentChecksum = await this.generateChecksum(file);
      return currentChecksum === expectedChecksum;
    } catch (error) {
      documentLogger.error('Error verifying file integrity:', error);
      return false;
    }
  }
}

// Factory function to create storage service
export function createDocumentStorageService(
  provider: 'local' | 's3',
  storageType: StorageType = 'local',
  config?: any
): DocumentStorageService {
  let storageProvider: DocumentStorageProvider;

  switch (provider) {
    case 'local':
      storageProvider = localDocumentStorage;
      break;
    case 's3':
      if (!config) {
        throw new Error('S3 configuration required');
      }
      storageProvider = new S3DocumentStorage(config);
      break;
    default:
      throw new Error(`Unsupported storage provider: ${provider}`);
  }

  return new DocumentStorageService(storageProvider, storageType);
}

// Export storage tier configuration for use in UI components
export { STORAGE_TIER_CONFIG };

// Export default instance (local storage for development)
// Note: Call documentStorageService.setStorageType() when subscription changes
export const documentStorageService = createDocumentStorageService('local', 'local'); 